import {useTranslation} from '@/translations/i18n'
import type {AgendaEventAppointment, AgendaEventType} from '@/features/agenda/types'
import {eventTypeMapToLabel} from '@/features/agenda/utils'
import {useAgendaNavigation} from '@/features/agenda/hooks/useAgendaNavigation'

export type TargetEntity = {
    entity: {
        fullName: string
    } | null
    // Set only from the patient's perspective, when the consultant has been replaced on this event
    substituteFullName: string | null
}

interface UseAgendaAppointmentEventDataProps {
    agendaEvent: AgendaEventAppointment | null
}

/**
 * Registered substitution to flag on the event card.
 * On the consultants' agenda it is relative to the consultant whose column the event is displayed in:
 * `replaced` when that consultant has been replaced by `counterpartFullName`,
 * `covering` when that consultant is the substitute covering for `counterpartFullName`.
 * On the patients' agenda there is one `replaced` entry per substituted consultant.
 */
export type AgendaEventSubstitution = {
    type: 'replaced' | 'covering'
    counterpartFullName: string
}

interface AgendaEventData {
    targetEntities: TargetEntity[]
    accessTypes: string
    fullNames: string
    sector?: AgendaEventType['sector']
    substitutions: AgendaEventSubstitution[]
}

export const useAgendaAppointmentEventData = ({agendaEvent}: UseAgendaAppointmentEventDataProps): AgendaEventData => {
    const {t} = useTranslation()
    const {
        params: {entity}
    } = useAgendaNavigation()

    if (!agendaEvent) return {targetEntities: [], accessTypes: '', fullNames: '', sector: undefined, substitutions: []}

    const {eventConsultants, eventPatients, isGroupTreatment, resourceId} = agendaEvent

    const targetEntities: TargetEntity[] =
        entity === 'patients'
            ? eventConsultants?.map(ep => ({entity: ep.user, substituteFullName: ep.substitute?.fullName ?? null})) ??
              []
            : eventPatients?.map(ec => ({entity: ec.patient, substituteFullName: null})) ?? []

    const accessTypes = isGroupTreatment
        ? t('agenda:group_treatment')
        : eventConsultants
          ? eventConsultants
                ?.filter(
                    (ec, index, self) =>
                        index === self.findIndex(e => e.accessType.accessType === ec.accessType.accessType)
                )
                ?.map(ec => t(eventTypeMapToLabel[ec.accessType.accessType]))
                .join(', ')
          : t('agenda:unknown_event_name')

    const fullNames = targetEntities
        ? targetEntities.length > 2
            ? `${targetEntities[0].entity?.fullName}, ${targetEntities[1].entity?.fullName} +${targetEntities.length - 2}`
            : targetEntities.map(ec => ec.entity?.fullName).join(', ')
        : ''

    const sector = agendaEvent.type == 'event' ? agendaEvent.sector : undefined

    const substitutions = ((): AgendaEventSubstitution[] => {
        if (entity === 'patients')
            return (
                eventConsultants?.flatMap(ec =>
                    ec.substitute ? [{type: 'replaced' as const, counterpartFullName: ec.substitute.fullName}] : []
                ) ?? []
            )

        // On the consultants' agenda the event is shown both in the replaced consultant's column and in the substitute's one
        if (!resourceId) return []
        const replacedConsultant = eventConsultants?.find(ec => ec.userId === resourceId && !!ec.substitute)
        if (replacedConsultant?.substitute)
            return [{type: 'replaced', counterpartFullName: replacedConsultant.substitute.fullName}]
        const coveredConsultant = eventConsultants?.find(ec => ec.substituteId === resourceId)
        if (coveredConsultant) return [{type: 'covering', counterpartFullName: coveredConsultant.user.fullName}]
        return []
    })()

    return {
        targetEntities,
        accessTypes,
        fullNames,
        sector,
        substitutions
    }
}
